import { useEffect, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardTitle,
  Field,
  Input,
  Select,
  Switch,
  Table,
  Td,
  Th,
  useToast,
} from "@/core/ui";
import type { Role } from "@/core/auth/types";
import {
  useCreateGroupMapping,
  useDeleteGroupMapping,
  useGroupMappings,
  useOidcConfig,
  useSaveOidcConfig,
} from "./api";

const ROLES: Role[] = ["player", "author", "reviewer", "tenant_admin"];

export function OidcSettings() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const config = useOidcConfig();
  const save = useSaveOidcConfig();
  const [form, setForm] = useState({
    enabled: false,
    name: "SSO",
    client_id: "",
    client_secret: "",
    server_url: "",
    scopes: "openid email profile",
    groups_claim: "groups",
  });

  useEffect(() => {
    if (config.data) {
      setForm({
        enabled: config.data.enabled,
        name: config.data.name,
        client_id: config.data.client_id,
        client_secret: "",
        server_url: config.data.server_url,
        scopes: config.data.scopes.join(" "),
        groups_claim: config.data.groups_claim,
      });
    }
  }, [config.data]);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    save.mutate(
      { ...form, scopes: form.scopes.split(/\s+/).filter(Boolean) },
      {
        onSuccess: () => toast(t("admin.saved"), "success"),
        onError: () => toast(t("app.error_generic"), "danger"),
      },
    );
  };

  return (
    <div className="bt-admin__panels">
      <Card>
        <CardTitle>{t("admin.oidc.title")}</CardTitle>
        <form onSubmit={submit}>
          <Switch
            id="oidc-enabled"
            checked={form.enabled}
            onChange={(v) => setForm({ ...form, enabled: v })}
            label={t("admin.oidc.enabled")}
          />
          <Field label={t("admin.oidc.name")} htmlFor="oidc-name">
            <Input
              id="oidc-name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </Field>
          <Field
            label={t("admin.oidc.server_url")}
            htmlFor="oidc-url"
            hint={t("admin.oidc.server_url_hint")}
          >
            <Input
              id="oidc-url"
              type="url"
              value={form.server_url}
              onChange={(e) => setForm({ ...form, server_url: e.target.value })}
            />
          </Field>
          <Field label={t("admin.oidc.client_id")} htmlFor="oidc-client-id">
            <Input
              id="oidc-client-id"
              value={form.client_id}
              onChange={(e) => setForm({ ...form, client_id: e.target.value })}
            />
          </Field>
          <Field
            label={t("admin.oidc.client_secret")}
            htmlFor="oidc-secret"
            hint={
              config.data?.is_configured
                ? t("admin.oidc.secret_set", { hint: config.data.secret_hint })
                : t("admin.oidc.secret_unset")
            }
          >
            <Input
              id="oidc-secret"
              type="password"
              autoComplete="new-password"
              placeholder="••••••••"
              value={form.client_secret}
              onChange={(e) => setForm({ ...form, client_secret: e.target.value })}
            />
          </Field>
          <Field label={t("admin.oidc.scopes")} htmlFor="oidc-scopes">
            <Input
              id="oidc-scopes"
              value={form.scopes}
              onChange={(e) => setForm({ ...form, scopes: e.target.value })}
            />
          </Field>
          <Field label={t("admin.oidc.groups_claim")} htmlFor="oidc-groups-claim">
            <Input
              id="oidc-groups-claim"
              value={form.groups_claim}
              onChange={(e) => setForm({ ...form, groups_claim: e.target.value })}
            />
          </Field>
          <div className="bt-admin__form-footer">
            <Button type="submit" loading={save.isPending}>
              {t("admin.save")}
            </Button>
            {config.data ? (
              <Badge tone={config.data.is_configured ? "success" : "warning"}>
                {config.data.is_configured
                  ? t("admin.oidc.configured")
                  : t("admin.oidc.not_configured")}
              </Badge>
            ) : null}
          </div>
        </form>
        <p className="bt-admin__note">
          {t("admin.oidc.backchannel_hint", {
            url: `${window.location.origin}/api/auth/backchannel-logout/`,
          })}
        </p>
      </Card>
      <GroupMappings roles={ROLES} />
    </div>
  );
}

function GroupMappings({ roles }: { roles: Role[] }) {
  const { t } = useTranslation();
  const mappings = useGroupMappings();
  const create = useCreateGroupMapping();
  const remove = useDeleteGroupMapping();
  const [claim, setClaim] = useState("");
  const [role, setRole] = useState<Role>("author");

  return (
    <Card>
      <CardTitle>{t("admin.mappings.title")}</CardTitle>
      <Alert tone="info">{t("admin.mappings.hint")}</Alert>
      <Table className="bt-admin__mappings-table">
        <thead>
          <tr>
            <Th>{t("admin.mappings.claim_value")}</Th>
            <Th>{t("admin.mappings.role")}</Th>
            <Th />
          </tr>
        </thead>
        <tbody>
          {(mappings.data ?? []).map((m) => (
            <tr key={m.id}>
              <Td>
                <code>{m.claim_value}</code>
              </Td>
              <Td>{t(`roles.${m.role}`)}</Td>
              <Td className="bt-table__cell--right">
                <Button variant="ghost" size="sm" onClick={() => remove.mutate(m.id)}>
                  {t("admin.delete")}
                </Button>
              </Td>
            </tr>
          ))}
        </tbody>
      </Table>
      <form
        className="bt-inline-form bt-admin__mapping-form"
        onSubmit={(e) => {
          e.preventDefault();
          if (!claim.trim()) return;
          create.mutate({ claim_value: claim.trim(), role }, { onSuccess: () => setClaim("") });
        }}
      >
        <div className="bt-inline-form__field">
          <Field label={t("admin.mappings.claim_value")} htmlFor="mapping-claim">
            <Input id="mapping-claim" value={claim} onChange={(e) => setClaim(e.target.value)} />
          </Field>
        </div>
        <div>
          <Field label={t("admin.mappings.role")} htmlFor="mapping-role">
            <Select
              id="mapping-role"
              value={role}
              onChange={(e) => setRole(e.target.value as Role)}
            >
              {roles.map((r) => (
                <option key={r} value={r}>
                  {t(`roles.${r}`)}
                </option>
              ))}
            </Select>
          </Field>
        </div>
        <Button
          type="submit"
          className="bt-inline-form__submit"
          variant="secondary"
          loading={create.isPending}
        >
          {t("admin.add")}
        </Button>
      </form>
    </Card>
  );
}
